import { retireWorkoutDraftCheckpoint } from '@/lib/fitnessDraftPersistence';
import { workoutDraftRetirementClientId } from '@/lib/fitnessDraftKeys';
import { workoutDraftKey } from '@/lib/fitnessDraft';
import { fitnessDraftWriterState, notifyFitnessDraftWrite } from '@/lib/fitnessDraftWriter';
import { projectFitnessLocalWorkoutRaw } from '@/lib/fitnessLocalWorkout';
import { canonicalDiscardArgs, type FitnessDraftDiscardArgs } from '../../supabase/functions/_shared/fitnessDraftDiscardDomain';
import { fitnessLocalWorkoutId, type FitnessLocalWorkoutEnvelope } from '../../supabase/functions/_shared/fitnessLocalWorkoutDomain';

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export interface FitnessDraftDiscardAuth {
  getUser(): PromiseLike<{ data: { user: { id: string } | null }; error: unknown }>;
  getSession(): PromiseLike<{ data: { session: { user: { id: string }; access_token: string } | null }; error: unknown }>;
}
export interface FitnessDraftDiscardOrigin {
  ownerId: string; sessionId: string; actionId?: string;
  auth: FitnessDraftDiscardAuth; isCurrent: () => boolean;
  storage?: () => DraftStorage; now?: () => number;
}
export interface FitnessDraftDiscardOptions extends FitnessDraftDiscardOrigin {
  actionId: string; args: unknown; hashRaw?: (raw: string) => Promise<string>;
}
export interface FitnessDraftDiscardReceipt {
  readonly version: 1; readonly owner: string; readonly sessionId: string; readonly actionId: string;
  readonly operationId: string; readonly rawSha256: string;
  readonly status: 'succeeded'; readonly createdAt: string; readonly confirmedAt: string;
  readonly acknowledged: boolean; readonly scope: 'browser_session_draft'; readonly evidence: 'local_storage_readback';
}
interface Journal {
  version: 1; owner: string; sessionId: string; actionId: string; operationId: string; rawSha256: string;
  status: 'pending' | 'succeeded'; createdAt: string; confirmedAt: string | null; acknowledged: boolean;
}
export interface PreparedFitnessDraftDiscard {
  readonly operationId: string;
  readonly summary: NonNullable<FitnessLocalWorkoutEnvelope['summary']>;
}
interface PreparedPrivate { options: FitnessDraftDiscardOptions; raw: string; hash: string; revision: number; preparedAt: number; storage: DraftStorage; busy: boolean }
const preparedPrivate = new WeakMap<PreparedFitnessDraftDiscard, PreparedPrivate>();
export const FITNESS_DRAFT_DISCARD_REVIEW_TTL_MS = 2 * 60 * 1000;
export const fitnessDraftDiscardJournalKey = (owner: string) => 'vyv_fitness_draft_discard:' + owner;
const fail = (code: string): never => { throw new Error(code); };
const nowOf = (options: FitnessDraftDiscardOrigin) => options.now?.() ?? Date.now();
const captureAuth = (auth: FitnessDraftDiscardAuth): FitnessDraftDiscardAuth => ({ getUser: auth.getUser.bind(auth), getSession: auth.getSession.bind(auth) });
const uuid = (value: unknown): value is string => fitnessLocalWorkoutId(value) && typeof value === 'string' && value === value.toLowerCase();
const iso = (value: unknown): value is string => typeof value === 'string' && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
const freeze = <T,>(value: T): T => {
  if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); }
  return value;
};
function assertOrigin(options: FitnessDraftDiscardOrigin): void {
  if (!uuid(options.ownerId) || !uuid(options.sessionId) || options.actionId !== undefined && !uuid(options.actionId) || !options.isCurrent()) fail('invalid_session');
}
async function authenticate(options: FitnessDraftDiscardOrigin): Promise<void> {
  assertOrigin(options);
  const user = await options.auth.getUser(); assertOrigin(options);
  if (user.error || user.data.user?.id !== options.ownerId) fail('invalid_session');
  const session = await options.auth.getSession(); assertOrigin(options);
  if (session.error || session.data.session?.user.id !== options.ownerId || !session.data.session.access_token) fail('invalid_session');
}
function getStorage(options: FitnessDraftDiscardOrigin): DraftStorage {
  try { return options.storage?.() ?? window.sessionStorage; } catch { return fail('storage_unavailable'); }
}
function readJournal(storage: DraftStorage, owner: string, now: number): Journal | null {
  let raw: string | null;
  try { raw = storage.getItem(fitnessDraftDiscardJournalKey(owner)); } catch { return fail('storage_unavailable'); }
  if (raw === null) return null;
  try {
    if (raw.length > 2048) return fail('invalid_journal');
    const entry = JSON.parse(raw) as Journal;
    const keys = ['version', 'owner', 'sessionId', 'actionId', 'operationId', 'rawSha256', 'status', 'createdAt', 'confirmedAt', 'acknowledged'];
    if (!entry || typeof entry !== 'object' || Array.isArray(entry) || Object.keys(entry).length !== keys.length || !keys.every(key => Object.prototype.hasOwnProperty.call(entry, key)) || entry.version !== 1 || entry.owner !== owner || !uuid(entry.owner) || !uuid(entry.sessionId) || !uuid(entry.actionId) || !uuid(entry.operationId) || !/^[a-f0-9]{64}$/.test(entry.rawSha256) || !['pending', 'succeeded'].includes(entry.status) || typeof entry.acknowledged !== 'boolean' || !iso(entry.createdAt) || Date.parse(entry.createdAt) > now + 30000 || entry.status === 'pending' && (entry.confirmedAt !== null || entry.acknowledged) || entry.status === 'succeeded' && (!iso(entry.confirmedAt) || Date.parse(entry.confirmedAt) < Date.parse(entry.createdAt) || Date.parse(entry.confirmedAt) > now + 30000)) return fail('invalid_journal');
    return entry;
  } catch { return fail('invalid_journal'); }
}
const matches = (entry: Journal, options: FitnessDraftDiscardOrigin) => entry.owner === options.ownerId && entry.sessionId === options.sessionId && (options.actionId === undefined || entry.actionId === options.actionId);
const receipt = (entry: Journal): FitnessDraftDiscardReceipt => {
  if (entry.status !== 'succeeded' || !entry.confirmedAt) return fail('outcome_unknown');
  return freeze({ ...entry, status: 'succeeded' as const, confirmedAt: entry.confirmedAt, scope: 'browser_session_draft' as const, evidence: 'local_storage_readback' as const });
};
async function hashRaw(raw: string): Promise<string> {
  const result = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return [...new Uint8Array(result)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}
function assertWriter(owner: string, revision: number): void {
  const current = fitnessDraftWriterState(owner);
  if (current.active !== 0) fail('writer_active');
  if (current.revision !== revision) fail('draft_changed');
}
function readRaw(storage: DraftStorage, owner: string): string {
  let raw: string | null;
  try { raw = storage.getItem(workoutDraftKey(owner)); } catch { return fail('storage_unavailable'); }
  if (raw === null) return fail('draft_absent');
  return raw;
}

/** Preparing is read-only. The raw resume draft stays in this module's WeakMap. */
export async function prepareFitnessDraftDiscard(options: FitnessDraftDiscardOptions): Promise<PreparedFitnessDraftDiscard> {
  options = { ...options, auth: captureAuth(options.auth) };
  assertOrigin(options);
  const revision = fitnessDraftWriterState(options.ownerId).revision;
  assertWriter(options.ownerId, revision);
  const args: FitnessDraftDiscardArgs = canonicalDiscardArgs(options.args, options.ownerId, new Date(nowOf(options)));
  await authenticate(options); assertWriter(options.ownerId, revision);
  const storage = getStorage(options), existing = readJournal(storage, options.ownerId, nowOf(options));
  if (existing && existing.status === 'pending') fail('outcome_unknown');
  if (existing && matches(existing, options)) fail('receipt_available');
  // A successfully acknowledged old operation can be replaced only by a new,
  // freshly reviewed operation. An unacknowledged receipt keeps its recovery.
  if (existing && !existing.acknowledged) fail('ack_pending');
  const raw = readRaw(storage, options.ownerId);
  let projected: FitnessLocalWorkoutEnvelope;
  try { projected = projectFitnessLocalWorkoutRaw(raw, options.ownerId, new Date(nowOf(options))); } catch { return fail('invalid_draft'); }
  if (JSON.stringify(projected.summary) !== JSON.stringify(args.snapshot.summary)) fail('snapshot_changed');
  const hash = await (options.hashRaw ?? hashRaw)(raw);
  if (!/^[a-f0-9]{64}$/.test(hash)) fail('invalid_hash');
  await authenticate(options); assertWriter(options.ownerId, revision);
  if (readRaw(storage, options.ownerId) !== raw) fail('draft_changed');
  const operationId = crypto.randomUUID();
  if (!uuid(operationId)) fail('invalid_operation');
  const prepared = freeze({ operationId, summary: structuredClone(projected.summary!) });
  preparedPrivate.set(prepared, { options: { ...options }, raw, hash, revision, preparedAt: nowOf(options), storage, busy: false });
  return prepared;
}

/** An unknown pending operation is never retried, including when its target is
 * absent. A succeeded receipt is read without inspecting/removing a new draft. */
export async function readFitnessDraftDiscardReceipt(options: FitnessDraftDiscardOrigin): Promise<FitnessDraftDiscardReceipt | null> {
  options = { ...options, auth: captureAuth(options.auth) };
  await authenticate(options);
  const storage = getStorage(options), raw = storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId));
  const entry = readJournal(storage, options.ownerId, nowOf(options));
  if (storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== raw) return fail('receipt_conflict');
  await authenticate(options);
  if (storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== raw) return fail('receipt_conflict');
  if (!entry) return null;
  if (entry.status === 'pending') return fail('outcome_unknown');
  return matches(entry, options) ? receipt(entry) : null;
}

export async function confirmFitnessDraftDiscard(prepared: PreparedFitnessDraftDiscard): Promise<FitnessDraftDiscardReceipt> {
  const state = preparedPrivate.get(prepared);
  if (!state) return fail('review_required');
  if (state.busy) return fail('discard_busy');
  state.busy = true;
  try {
    const { options, storage } = state;
    await authenticate(options);
    const existing = readJournal(storage, options.ownerId, nowOf(options));
    if (existing?.status === 'pending') return fail('outcome_unknown');
    if (existing && matches(existing, options)) {
      if (existing.operationId !== prepared.operationId || existing.rawSha256 !== state.hash) return fail('receipt_conflict');
      return receipt(existing);
    }
    if (existing && !existing.acknowledged) return fail('ack_pending');
    const age = nowOf(options) - state.preparedAt;
    if (age < 0 || age > FITNESS_DRAFT_DISCARD_REVIEW_TTL_MS) return fail('review_expired');
    assertWriter(options.ownerId, state.revision);
    if (readRaw(storage, options.ownerId) !== state.raw) return fail('draft_changed');
    // Discard also accepts a validated legacy draft without a restore client ID.
    // A fresh operation ID can tombstone only an absent/retired durable record;
    // the persistence core rejects replacing a different live checkpoint.
    const clientId = workoutDraftRetirementClientId(state.raw, prepared.operationId);
    const durableGuard = () => {
      assertOrigin(options); assertWriter(options.ownerId, state.revision);
      const ageNow = nowOf(options) - state.preparedAt;
      if (ageNow < 0 || ageNow > FITNESS_DRAFT_DISCARD_REVIEW_TTL_MS || readRaw(storage, options.ownerId) !== state.raw) return fail('draft_changed');
      return true;
    };
    await retireWorkoutDraftCheckpoint(options.ownerId, clientId, durableGuard);
    await authenticate(options);
    // No await from the final exact-byte CAS through the verified journal and
    // remove/readback. Same-tab writers cannot interleave this critical section.
    const finalAge = nowOf(options) - state.preparedAt;
    if (finalAge < 0 || finalAge > FITNESS_DRAFT_DISCARD_REVIEW_TTL_MS) return fail('review_expired');
    assertOrigin(options); assertWriter(options.ownerId, state.revision);
    if (readRaw(storage, options.ownerId) !== state.raw) return fail('draft_changed');
    const previousJournal = storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId));
    if (JSON.stringify(readJournal(storage, options.ownerId, nowOf(options))) !== JSON.stringify(existing)) return fail('journal_changed');
    const pending: Journal = { version: 1, owner: options.ownerId, sessionId: options.sessionId, actionId: options.actionId, operationId: prepared.operationId, rawSha256: state.hash, status: 'pending', createdAt: new Date(nowOf(options)).toISOString(), confirmedAt: null, acknowledged: false };
    const pendingRaw = JSON.stringify(pending);
    if (storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== previousJournal) return fail('journal_changed');
    try {
      storage.setItem(fitnessDraftDiscardJournalKey(options.ownerId), pendingRaw);
      if (storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== pendingRaw) return fail('outcome_unknown');
      assertOrigin(options); assertWriter(options.ownerId, state.revision);
      if (storage.getItem(workoutDraftKey(options.ownerId)) !== state.raw) return fail('outcome_unknown');
      storage.removeItem(workoutDraftKey(options.ownerId));
      if (storage.getItem(workoutDraftKey(options.ownerId)) !== null) return fail('outcome_unknown');
      const succeeded: Journal = { ...pending, status: 'succeeded', confirmedAt: new Date(nowOf(options)).toISOString() };
      const succeededRaw = JSON.stringify(succeeded);
      storage.setItem(fitnessDraftDiscardJournalKey(options.ownerId), succeededRaw);
      if (storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== succeededRaw) return fail('outcome_unknown');
      notifyFitnessDraftWrite(options.ownerId);
      return receipt(succeeded);
    } catch { return fail('outcome_unknown'); }
  } finally { state.busy = false; }
}

/** Only the locally committed chat bubble acknowledges this private receipt. */
export async function acknowledgeFitnessDraftDiscardReceipt(options: FitnessDraftDiscardOrigin, expected: FitnessDraftDiscardReceipt): Promise<void> {
  options = { ...options, auth: captureAuth(options.auth) }; expected = structuredClone(expected);
  await authenticate(options);
  const storage = getStorage(options), entry = readJournal(storage, options.ownerId, nowOf(options));
  if (!entry || !matches(entry, options) || entry.status !== 'succeeded' || !entry.acknowledged && expected.acknowledged || JSON.stringify({ ...receipt(entry), acknowledged: expected.acknowledged }) !== JSON.stringify(expected)) return fail('receipt_conflict');
  assertOrigin(options);
  if (entry.acknowledged) return;
  const raw = storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId));
  const acknowledged = JSON.stringify({ ...entry, acknowledged: true });
  if (JSON.stringify(readJournal(storage, options.ownerId, nowOf(options))) !== JSON.stringify(entry) || storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== raw) return fail('receipt_conflict');
  try { storage.setItem(fitnessDraftDiscardJournalKey(options.ownerId), acknowledged); if (storage.getItem(fitnessDraftDiscardJournalKey(options.ownerId)) !== acknowledged) fail('ack_pending'); }
  catch { fail('ack_pending'); }
}
